import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Plug, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import {
  CHROME_STORE_URL,
  ExtensionError,
  beginPairing,
  deliverPairingCode,
  isLocalDashboard,
  resolveExtensionId,
} from "@/lib/extension";

const SCOPES = [
  "Read the clients and sites in your workspace",
  "Create a client or site while filing an audit",
  "Save audit results and their findings to your workspace",
];

export default function ExtensionAuthorize() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [status, setStatus] = useState("idle");
  const [failure, setFailure] = useState(null);

  // The extension appends `ext` when it opens this page. Arriving from the
  // sidebar instead falls back to the id this build was configured with.
  const resolved = useMemo(() => resolveExtensionId(params.get("ext")), [params]);
  const local = isLocalDashboard();

  /**
   * Three steps, in this order for a reason: the extension opens a pairing
   * window first, so a code is only ever minted for a pairing it is waiting on,
   * and a code that never reaches it expires two minutes later unused.
   */
  const authorize = async () => {
    setStatus("sending");
    setFailure(null);
    try {
      const state = await beginPairing(resolved.id);
      const { data } = await api.post("/extension/pair", { extension_id: resolved.id });
      await deliverPairingCode(resolved.id, data.code, state);
      setStatus("done");
      // No extension_connected here. Reaching this line means the code was
      // handed to the extension, not that the extension redeemed it; the
      // server captures the event in routes.py when the redemption lands.
    } catch (err) {
      setStatus("idle");
      setFailure(
        err instanceof ExtensionError
          ? { code: err.code, message: err.message }
          : { code: "server", message: err.response?.data?.detail || err.message || "Pairing failed." }
      );
    }
  };

  return (
    <div data-testid="extension-authorize-page">
      <PageHeader
        title="Connect the Complaudax extension"
        description="Approve the extension once so the audits you run in your browser are filed to this workspace."
      />

      <div className="max-w-2xl">
      {resolved.error ? (
        <div
          className="bg-white border border-[#C62828] rounded-sm p-5"
          role="alert"
          data-testid="extension-authorize-blocked"
        >
          <p className="text-sm font-medium mb-1">This request was not approved</p>
          <p className="text-sm text-ink-muted">{resolved.error}</p>
        </div>
      ) : status === "done" ? (
        <div
          className="bg-white border border-gray-200 rounded-sm p-5"
          role="status"
          data-testid="extension-authorize-success"
        >
          <div className="flex items-start gap-2.5">
            <CheckCircle2 size={18} strokeWidth={1.5} className="text-[#2E7D32] mt-0.5 shrink-0" aria-hidden="true" />
            <div>
              <p className="text-sm font-medium mb-1">Extension connected</p>
              <p className="text-sm text-ink-muted">
                Return to the Complaudax side panel. It now shows {user?.email} and can file audits against
                your clients and sites. You can close this tab.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-sm p-5" data-testid="extension-authorize-consent">
          <div className="flex items-start gap-2.5 pb-4 border-b border-gray-200">
            <Plug size={18} strokeWidth={1.5} className="text-[#16324F] mt-0.5 shrink-0" aria-hidden="true" />
            <div className="min-w-0">
              <p className="text-sm font-medium">The Complaudax extension is asking to connect</p>
              <p className="text-sm text-ink-muted">
                Signed in as <span className="font-mono text-xs">{user?.email}</span>
              </p>
            </div>
          </div>

          <p className="text-sm font-medium mt-4 mb-2">It will be able to:</p>
          <ul className="text-sm text-ink-muted space-y-1.5 mb-4">
            {SCOPES.map((scope) => (
              <li key={scope} className="flex items-start gap-2">
                <ShieldCheck size={15} strokeWidth={1.5} className="mt-0.5 shrink-0" aria-hidden="true" />
                {scope}
              </li>
            ))}
          </ul>
          <p className="text-xs text-ink-muted mb-5">
            You can revoke this at any time with "Disconnect" in the side panel. Extension ID{" "}
            <span className="font-mono" data-testid="extension-authorize-id">{resolved.id}</span>.
          </p>

          <div className="flex items-center gap-3">
            <button
              data-testid="extension-authorize-btn"
              onClick={authorize}
              disabled={status === "sending"}
              className="bg-[#16324F] text-white rounded-sm px-4 py-2 text-sm hover:bg-[#10243a] transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] focus-visible:ring-offset-2"
            >
              {status === "sending" ? "Connecting..." : "Authorize"}
            </button>
            <Link
              to="/"
              data-testid="extension-authorize-cancel"
              className="text-sm text-ink-muted underline hover:text-ink rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F]"
            >
              Cancel
            </Link>
          </div>

          {failure && (
            <div className="mt-4 border-l-2 border-[#C62828] pl-3" role="alert" data-testid="extension-authorize-error">
              <p className="text-sm">{failure.message}</p>
              {failure.code === "unreachable" && (
                <p className="text-sm text-ink-muted mt-1">
                  Check that the extension is installed and enabled, then try again.{" "}
                  <a
                    href={CHROME_STORE_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="underline text-[#16324F] rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F]"
                  >
                    Get the extension
                  </a>
                </p>
              )}
              {failure.code === "unreachable" && local && (
                <p className="text-sm text-[#B45309] mt-1">
                  You are on {window.location.host}. A release package ignores this origin: build the
                  extension with{" "}
                  <code className="font-mono text-xs bg-gray-100 border border-gray-200 rounded-sm px-1.5 py-0.5">
                    yarn build:dev
                  </code>{" "}
                  and reload it.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <p className="text-xs text-ink-muted mt-6">
        Prefer to start from the browser? Open the Complaudax side panel and press "Connect to
        Complaudax"; it brings you back here with the right extension selected.
      </p>
      </div>
    </div>
  );
}
